"use client";
import React, { useEffect, useRef, useState } from "react";

interface ReviewForm {
  name: string;
  email: string;
  rating: number;
  text: string;
  paket: string;
}

function StarRatingInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Bewertung">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={`${star} Stern${star > 1 ? "e" : ""}`}
          className="focus:outline-none"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
        >
          <svg
            className={`w-8 h-8 transition-colors ${
              star <= (hovered || value) ? "text-yellow-400" : "text-gray-600"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

export default function KundenstimmenSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ReviewForm>({ name: "", email: "", rating: 0, text: "", paket: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = "1";
            (entry.target as HTMLElement).style.transform = "translateY(0)";
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  // Close modal on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setModalOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) { setError("Bitte wähle eine Sternebewertung aus."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data.error || "Fehler beim Senden. Bitte versuche es erneut.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSent(false);
    setError("");
    setForm({ name: "", email: "", rating: 0, text: "", paket: "" });
  };

  return (
    <section
      ref={sectionRef}
      id="kundenstimmen"
      className="relative py-20 md:py-28 bg-[#08080F] overflow-hidden"
      aria-label="Kundenstimmen"
    >
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full opacity-10 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse, #6366f1 0%, #8b5cf6 40%, transparent 70%)",
          filter: "blur(80px)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className="text-center mb-12"
          style={{ opacity: 0, transform: "translateY(30px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase bg-violet-500/10 text-violet-400 border border-violet-500/20 mb-4">
            Bewertungen
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            Kunden&shy;stimmen
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
            Was meine Kunden über ihre Builds sagen.
          </p>
        </div>

        {/* Empty State */}
        <div className="flex flex-col items-center justify-center gap-6 py-16 px-4 rounded-2xl bg-white/[0.03] border border-white/10 max-w-xl mx-auto text-center">
          <div className="flex gap-1" aria-hidden="true">
            {[1,2,3,4,5].map((s) => (
              <svg key={s} className="w-8 h-8 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <p className="text-white/60 text-lg font-medium">Noch keine Bewertungen – sei der Erste!</p>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-[#08080F]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            Bewertung abgeben
          </button>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Bewertung abgeben"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Modal Panel */}
          <div className="relative z-10 w-full max-w-lg bg-[#0f0f1a] border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none"
              aria-label="Schliessen"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {sent ? (
              <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                  <svg className="w-8 h-8 text-violet-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Danke für deine Bewertung!</h3>
                <p className="text-gray-400 text-sm">Deine Bewertung wurde erfolgreich übermittelt.</p>
                <button
                  onClick={closeModal}
                  className="mt-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm transition-colors"
                >
                  Schliessen
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-1">Bewertung abgeben</h3>
                <p className="text-gray-400 text-sm mb-6">Teile deine Erfahrung mit dem PC Build Service.</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                      Name <span className="text-violet-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Dein Name"
                      className="form-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                      E-Mail <span className="text-violet-400">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="deine@email.ch"
                      className="form-input"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>

                  {/* Star Rating */}
                  <div>
                    <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                      Bewertung <span className="text-violet-400">*</span>
                    </label>
                    <StarRatingInput value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
                  </div>

                  {/* Review Text */}
                  <div>
                    <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                      Bewertungstext <span className="text-violet-400">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      placeholder="Beschreibe deine Erfahrung..."
                      className="form-input resize-none"
                      value={form.text}
                      onChange={(e) => setForm({ ...form, text: e.target.value })}
                    />
                  </div>

                  {/* Package (optional) */}
                  <div>
                    <label className="block font-mono text-[10px] text-white/35 uppercase tracking-widest mb-2">
                      Welches Paket hast du gebucht? <span className="text-white/20">(optional)</span>
                    </label>
                    <select
                      className="form-input"
                      value={form.paket}
                      onChange={(e) => setForm({ ...form, paket: e.target.value })}
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="Basic Build – 120 CHF">Basic Build – 120 CHF</option>
                      <option value="Setup Build – 180 CHF">Setup Build – 180 CHF</option>
                      <option value="Premium Build – 250 CHF">Premium Build – 250 CHF</option>
                      <option value="Budget PC Build – 100 CHF + Build-Paket">Budget PC Build – 100 CHF + Build-Paket</option>
                      <option value="Nur Add-ons / Einzelleistung">Nur Add-ons / Einzelleistung</option>
                      <option value="Sonstiges / Beratung">Sonstiges / Beratung</option>
                    </select>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500/20 bg-red-500/[0.06]">
                      <span className="text-xs text-red-400">{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        </svg>
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Bewertung absenden
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
